import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Download, Plus, QrCode, Upload, XCircle } from 'lucide-react';
import { animalsApi } from '../../services/resources';
import { getApiErrorMessage } from '../../services/api';
import { useReferenceData } from '../../features/common/referenceData';
import { useDebounce } from '../../hooks/useDebounce';
import { ILMSPageHeader } from '../../components/ILMSPageHeader';
import { ILMSCard } from '../../components/ILMSCard';
import { ILMSButton } from '../../components/ILMSButton';
import { ILMSTable, type ILMSTableColumn } from '../../components/ILMSTable';
import { ILMSFilterBar } from '../../components/ILMSFilterBar';
import { ILMSStatusBadge } from '../../components/ILMSStatusBadge';
import { DEFAULT_PAGE_SIZE, ILMSPagination } from '../../components/ILMSPagination';
import { sortRows, type TableSort } from '../../utils/tableSort';
import { ILMSSelect } from '../../components/ILMSForm';
import { ILMSModal } from '../../components/ILMSModal';
import { ILMSAlert } from '../../components/ILMSAlert';
import { formatAge, formatNumber } from '../../utils/format';
import type { Animal, AnimalHealthStatus, BulkImportResult } from '../../types';

function BulkImportModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const photosZipRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [photosZip, setPhotosZip] = useState<File | null>(null);
  const [result, setResult] = useState<BulkImportResult | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const columnsQuery = useQuery({
    queryKey: ['animals', 'bulk-import-columns'],
    queryFn: () => animalsApi.bulkImportColumns(),
    enabled: open,
    staleTime: 60 * 60_000,
  });

  const reset = () => {
    setSelectedFile(null);
    setPhotosZip(null);
    setResult(null);
    setDownloadError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (photosZipRef.current) photosZipRef.current.value = '';
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const downloadTemplate = async () => {
    setDownloadError(null);
    try {
      const blob = await animalsApi.downloadBulkImportTemplate();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'animal_bulk_import_template.csv';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setDownloadError(getApiErrorMessage(err, 'Unable to download the template. Please try again.'));
    }
  };

  const importMutation = useMutation({
    mutationFn: () => animalsApi.bulkImport(selectedFile!, photosZip),
    onSuccess: (data) => {
      setResult(data);
      if (data.succeeded > 0) {
        queryClient.invalidateQueries({ queryKey: ['animals'] });
        queryClient.invalidateQueries({ queryKey: ['reference', 'animals'] });
      }
    },
  });

  return (
    <ILMSModal
      open={open}
      onClose={handleClose}
      title="Bulk Import Animals"
      size="lg"
      footer={
        <>
          <ILMSButton variant="ghost" onClick={handleClose}>Close</ILMSButton>
          {!result && (
            <ILMSButton
              icon={<Upload />}
              disabled={!selectedFile}
              loading={importMutation.isPending}
              onClick={() => importMutation.mutate()}
            >
              Upload &amp; Import
            </ILMSButton>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-5">
        <p className="text-body text-ink-700">
          For onboarding many animals at once — e.g. a herd of a hundred cows arriving the same day — fill in one
          row per animal in the template below, then upload it here. Each row is checked independently, so a mistake
          in one row won't block the rest.
        </p>

        <div className="rounded-md border border-ink-300/20 p-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
          <div>
            <p className="text-card-title">Step 1 — Download the template</p>
            <p className="text-secondary text-ink-500">A ready-to-fill CSV with the correct columns and two example rows.</p>
          </div>
          <ILMSButton variant="outline" icon={<Download />} onClick={downloadTemplate}>
            Download Sample CSV
          </ILMSButton>
        </div>
        {downloadError && <ILMSAlert tone="critical">{downloadError}</ILMSAlert>}

        {!result && (
          <details className="rounded-md border border-ink-300/20 p-4 group">
            <summary className="text-card-title cursor-pointer select-none">
              Not sure what a column means? Click to see what each one is for
            </summary>
            <div className="mt-3 divide-y divide-ink-300/10 border-t border-ink-300/10">
              {(columnsQuery.data ?? []).map((c) => (
                <div key={c.column} className="py-2.5 grid grid-cols-1 sm:grid-cols-[10rem_1fr] gap-x-3 gap-y-0.5">
                  <p className="text-secondary font-semibold text-ink-900 flex items-center gap-1.5">
                    {c.column}
                    {c.required && <span className="text-helper-text text-critical-600">*required</span>}
                  </p>
                  <p className="text-secondary text-ink-600">{c.description}</p>
                </div>
              ))}
              {columnsQuery.isLoading && <p className="text-secondary text-ink-400 py-2">Loading…</p>}
            </div>
          </details>
        )}

        {!result && (
          <div className="rounded-md border border-ink-300/20 p-4">
            <p className="text-card-title mb-1">Step 2 — Upload your filled-in CSV</p>
            <p className="text-secondary text-ink-500 mb-3">
              animal_type, breed and location must already exist in the system (names, not IDs) — set those up first
              under Assets Registry / Farm Setup if needed.
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
              className="text-secondary text-ink-700 file:mr-3 file:rounded-md file:border-0 file:bg-brand-green-50 file:px-3.5 file:py-2 file:text-sm file:font-medium file:text-brand-green-700 hover:file:bg-brand-green-100"
            />
            {importMutation.isError && (
              <ILMSAlert tone="critical" className="mt-3">
                {getApiErrorMessage(importMutation.error, 'Unable to import this file. Please check the format and try again.')}
              </ILMSAlert>
            )}
          </div>
        )}

        {!result && (
          <div className="rounded-md border border-ink-300/20 p-4">
            <p className="text-card-title mb-1">Step 3 — Photos (optional)</p>
            <p className="text-secondary text-ink-500 mb-3">
              A CSV cell can't hold a photo, so if you listed <code>photo_filenames</code> for any row, zip up all
              those image files together and upload the ZIP here — file names inside must match exactly what you
              typed in the CSV. Skip this if you'd rather add photos to each animal afterward.
            </p>
            <input
              ref={photosZipRef}
              type="file"
              accept=".zip,application/zip"
              onChange={(e) => setPhotosZip(e.target.files?.[0] ?? null)}
              className="text-secondary text-ink-700 file:mr-3 file:rounded-md file:border-0 file:bg-brand-green-50 file:px-3.5 file:py-2 file:text-sm file:font-medium file:text-brand-green-700 hover:file:bg-brand-green-100"
            />
          </div>
        )}

        {result && (
          <div className="flex flex-col gap-4">
            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="rounded-md bg-surface-muted p-3">
                <p className="text-kpi-number text-ink-900">{result.total}</p>
                <p className="text-helper-text text-ink-500">Rows processed</p>
              </div>
              <div className="rounded-md bg-success-50 p-3">
                <p className="text-kpi-number text-success-700">{result.succeeded}</p>
                <p className="text-helper-text text-success-700">Added</p>
              </div>
              <div className="rounded-md bg-critical-50 p-3">
                <p className="text-kpi-number text-critical-700">{result.failed}</p>
                <p className="text-helper-text text-critical-700">Failed</p>
              </div>
            </div>

            {result.succeeded > 0 && (
              <ILMSAlert tone="success">
                <span className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 shrink-0" />
                  Added: {result.created_animal_ids.join(', ')}
                </span>
              </ILMSAlert>
            )}

            {result.created_calf_ids.length > 0 && (
              <ILMSAlert tone="success">
                <span className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 shrink-0" />
                  Also recorded {result.created_calf_ids.length} calf birth{result.created_calf_ids.length > 1 ? 's' : ''}: {result.created_calf_ids.join(', ')}
                </span>
              </ILMSAlert>
            )}

            {result.warnings.length > 0 && (
              <ILMSAlert tone="warning">
                <div className="flex flex-col gap-1">
                  <span className="font-semibold">Some photos couldn't be attached:</span>
                  {result.warnings.map((w, i) => <span key={i}>{w.message}</span>)}
                </div>
              </ILMSAlert>
            )}

            {result.animals_missing_photos.length > 0 && (
              <div className="rounded-md border border-brand-gold-200 bg-brand-gold-50 p-3.5">
                <p className="text-secondary font-semibold text-ink-900 mb-1">
                  These animals still need at least 4 photos:
                </p>
                <div className="flex flex-wrap gap-2">
                  {result.animals_missing_photos.map((a) => (
                    <button
                      key={a.id}
                      onClick={() => navigate(`/animals/${a.id}/edit`)}
                      className="text-secondary px-2.5 py-1 rounded-full bg-white border border-brand-gold-300 text-brand-gold-700 hover:bg-brand-gold-100"
                    >
                      {a.animal_id} — add photos
                    </button>
                  ))}
                </div>
              </div>
            )}

            {result.errors.length > 0 && (
              <div>
                <p className="text-card-title mb-2 flex items-center gap-2 text-critical-700">
                  <XCircle className="h-4 w-4" /> Rows that need fixing
                </p>
                <div className="rounded-md border border-critical-100 divide-y divide-critical-100">
                  {result.errors.map((e) => (
                    <div key={e.row} className="p-3 text-secondary">
                      <span className="font-semibold text-ink-900">Row {e.row}: </span>
                      <span className="text-ink-700">{e.message}</span>
                    </div>
                  ))}
                </div>
                <ILMSButton variant="outline" className="mt-3" onClick={reset}>
                  Fix and try again
                </ILMSButton>
              </div>
            )}
          </div>
        )}
      </div>
    </ILMSModal>
  );
}

const HEALTH_STATUSES: AnimalHealthStatus[] = ['Active', 'Quarantine', 'Hospitalized', 'Sold', 'Returned', 'Dead', 'Disposed'];

export default function AnimalsList() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [healthStatus, setHealthStatus] = useState('');
  const [animalTypeId, setAnimalTypeId] = useState('');
  const [breedId, setBreedId] = useState('');
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  const debouncedSearch = useDebounce(search, 350);

  const { data: refData } = useReferenceData(['animalTypes', 'breeds']);

  // The whole herd in one go (ID order, COW-000001 first), paged and sorted
  // here, so a header click sorts every animal, not just one page.
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sort, setSort] = useState<TableSort | null>(null);
  const listQuery = useQuery({
    queryKey: ['animals', 'all', debouncedSearch, healthStatus, animalTypeId, breedId],
    queryFn: () =>
      animalsApi.list({
        page: 1,
        page_size: 2000,
        sort: 'id',
        q: debouncedSearch || undefined,
        health_status: healthStatus || undefined,
        animal_type_id: animalTypeId || undefined,
        breed_id: breedId || undefined,
      }),
  });

  const activeCount = [healthStatus, animalTypeId, breedId].filter(Boolean).length;

  const handleExport = async () => {
    setExportError(null);
    try {
      const blob = await animalsApi.exportCsv({
        q: debouncedSearch || undefined,
        health_status: healthStatus || undefined,
        animal_type_id: animalTypeId || undefined,
        breed_id: breedId || undefined,
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `animals_export_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setExportError(getApiErrorMessage(err, 'Unable to export animals. Please try again.'));
    }
  };

  const columns: ILMSTableColumn<Animal>[] = [
    {
      key: 'animal_id',
      header: 'Animal ID',
      primary: true,
      // One line - the asset code shows only when it isn't the same as the animal ID.
      render: (a) => (
        <span className="font-semibold text-ink-900">
          {a.animal_id}
          {a.asset_code && a.asset_code !== a.animal_id ? <span className="ml-1.5 font-normal text-helper-text text-ink-400">{a.asset_code}</span> : null}
        </span>
      ),
    },
    { key: 'name', header: 'Name', render: (a) => a.animal_name || '—' },
    { key: 'tag_id', header: 'Tag ID', render: (a) => a.primary_tag || '—', hideOnTablet: true },
    { key: 'type', header: 'Type', render: (a) => a.animal_type_name ?? '—' },
    { key: 'breed', header: 'Breed', render: (a) => a.breed_name ?? '—', hideOnTablet: true },
    { key: 'gender', header: 'Gender', render: (a) => a.gender },
    { key: 'age', header: 'Age', render: (a) => formatAge(a.date_of_birth), hideOnTablet: true,
      sortValue: (a) => (a.date_of_birth ? -Date.parse(a.date_of_birth) : null) },
    { key: 'weight', header: 'Weight (kg)', render: (a) => formatNumber(a.current_weight_kg, 1), align: 'right' },
    { key: 'health', header: 'Health', render: (a) => <ILMSStatusBadge status={a.health_status} size="sm" /> },
    { key: 'production', header: 'Production', render: (a) => <ILMSStatusBadge status={a.production_status} size="sm" /> },
  ];

  const allRows = sortRows(listQuery.data?.items ?? [], sort, columns);
  const pageRows = allRows.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div className="flex flex-col gap-3">
      <ILMSPageHeader
        title="Animals"
        subtitle="Manage your livestock records, identification and lifecycle."
        actions={
          <>
            <ILMSButton variant="outline" icon={<QrCode />} onClick={() => navigate('/qr-scan')}>
              Scan QR
            </ILMSButton>
            <ILMSButton variant="outline" icon={<Upload />} onClick={() => setBulkImportOpen(true)}>
              Bulk Import
            </ILMSButton>
            <ILMSButton variant="outline" icon={<Download />} onClick={handleExport}>
              Export CSV
            </ILMSButton>
            <ILMSButton icon={<Plus />} onClick={() => navigate('/animals/new')}>
              Add Animal
            </ILMSButton>
          </>
        }
      />

      <BulkImportModal open={bulkImportOpen} onClose={() => setBulkImportOpen(false)} />

      {exportError && <ILMSAlert tone="critical">{exportError}</ILMSAlert>}

      <ILMSCard padded={false}>
        <div className="p-3 border-b border-ink-300/10">
          <ILMSFilterBar
            searchValue={search}
            onSearchChange={(v) => {
              setSearch(v);
              setPage(1);
            }}
            searchPlaceholder="Search by animal ID or name…"
            activeCount={activeCount}
            onClear={() => {
              setHealthStatus('');
              setAnimalTypeId('');
              setBreedId('');
            }}
          >
            <ILMSSelect value={healthStatus} onChange={(e) => { setHealthStatus(e.target.value); setPage(1); }} className="w-full lg:w-44">
              <option value="">All health statuses</option>
              {HEALTH_STATUSES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </ILMSSelect>
            <ILMSSelect value={animalTypeId} onChange={(e) => { setAnimalTypeId(e.target.value); setPage(1); }} className="w-full lg:w-44">
              <option value="">All animal types</option>
              {refData.animalTypes.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </ILMSSelect>
            <ILMSSelect value={breedId} onChange={(e) => { setBreedId(e.target.value); setPage(1); }} className="w-full lg:w-44">
              <option value="">All breeds</option>
              {refData.breeds.map((b) => (
                <option key={b.value} value={b.value}>{b.label}</option>
              ))}
            </ILMSSelect>
          </ILMSFilterBar>
        </div>
        <div className="px-3 pt-1 pb-3">
          <ILMSTable
            columns={columns}
            data={pageRows}
            sort={sort}
            onSortChange={(s) => { setSort(s); setPage(1); }}
            rowKey={(a) => a.id}
            // Every animal matching the filters, not just this page.
            exportTitle="Animals" exportRows={async () => allRows}
            onRowClick={(a) => navigate(`/animals/${a.id}`)}
            isLoading={listQuery.isLoading}
            error={listQuery.isError ? getApiErrorMessage(listQuery.error) : null}
            onRetry={() => listQuery.refetch()}
            emptyTitle="No animals found."
            emptyDescription={
              debouncedSearch || activeCount > 0
                ? 'Try a different search term or clear your filters.'
                : 'Add your first animal to start tracking its health, production and history.'
            }
            emptyActionLabel={!debouncedSearch && activeCount === 0 ? 'Add Animal' : undefined}
            onEmptyAction={() => navigate('/animals/new')}
          />
          {!listQuery.isLoading && !listQuery.isError && allRows.length > 0 && (
            <ILMSPagination page={page} pageSize={pageSize} total={allRows.length} onPageChange={setPage}
              onPageSizeChange={(size) => { setPageSize(size); setPage(1); }} />
          )}
        </div>
      </ILMSCard>
    </div>
  );
}
